import React, { CSSProperties, Component, createRef, RefObject } from 'react'
import {
  API,
  IPaymentQueueWithExternalSource,
  PaymentQueueStatus,
  convertToRegularBarcodeIfBatch,
  IPaymentLine,
  PaymentQueueOrderType,
  ExternalSourceName,
  CreatePayment,
  IPOSListing,
  IPaymentQueueLine,
  IPayment,
  cleanBarcode,
  productName,
  ProductType,
} from '@getgreenline/homi-shared'
import { Button, Modal, message } from 'antd'
import { BarcodeListener } from '../../../../components/BarcodeListener'
import { ParkedSaleStore } from './ParkedSaleStore'
import { observer } from 'mobx-react'
import {
  CreateBarcodeValidation,
  ValidationLine,
} from '@getgreenline/homi-shared/dist/models/Payments/CreatePaymentLine'
import { CreateValidationLines } from './CreateValidationLines'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { getTotalProductsToValidate } from '../../../../utilities/paymentsHelper'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'

interface Props {
  parkedSaleStore: ParkedSaleStore
  isValidationModalOpen: boolean
  setValidationModalOpen: (bool: boolean) => void
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface States {
  paymentInfo: IPayment | null
  createBarcodeValidation?: CreateBarcodeValidation<IPaymentLine | IPaymentQueueLine>
  refValidationContainer: RefObject<HTMLDivElement>
}

@observer
export class ParkedSaleValidationModal extends Component<Props & InjectedProps, States> {
  parkedSaleModalQueue = this.props.parkedSaleStore.parkedSaleModalQueue

  constructor(props: Props & InjectedProps) {
    super(props)
    this.state = {
      paymentInfo: null,
      createBarcodeValidation: undefined,
      refValidationContainer: createRef(),
    }
  }

  componentDidMount = async () => {
    await this.createBarcodeValidationLines()
  }

  createBarcodeValidationLines = async () => {
    const createBarcodeValidation = new CreateBarcodeValidation<IPaymentLine | IPaymentQueueLine>()
    const { company, flatPOSListing } = this.props.parkedSaleStore
    const productsMap = new Map(flatPOSListing.map((product) => [product.id, product]))

    if (!this.parkedSaleModalQueue || !company) return

    if (this.parkedSaleModalQueue.completedPayment) {
      await this.addCompletedPaymentLine(
        createBarcodeValidation,
        productsMap,
        this.parkedSaleModalQueue.completedPayment.id,
      )
    } else {
      await this.addIncompletedPaymentLine(createBarcodeValidation, productsMap)
    }
  }

  addCompletedPaymentLine = async (
    createBarcodeValidation: CreateBarcodeValidation<IPaymentLine | IPaymentQueueLine>,
    productsMap: Map<string, IPOSListing>,
    completedPaymentId: string,
  ) => {
    const { company } = this.props.parkedSaleStore

    if (!this.parkedSaleModalQueue || !company) return

    const paymentInfo = await API.getPaymentById(company.id, completedPaymentId)

    for (const paymentLine of paymentInfo.paymentLines) {
      const targetQueue = this.parkedSaleModalQueue?.paymentLines.find((queue) => {
        return queue.productId !== null && queue.productId === paymentLine.productId
      })

      // Add the product that has the barcode
      if (targetQueue && targetQueue.barcode) {
        const validationLine = createBarcodeValidation.addValidationLine(
          { ...paymentLine, barcode: targetQueue.barcode },
          paymentLine.units,
        )

        this.processNonProductValidationLine(validationLine, productsMap, paymentLine.productId)
      }
    }

    this.setState({ paymentInfo, createBarcodeValidation })
  }

  addIncompletedPaymentLine = async (
    createBarcodeValidation: CreateBarcodeValidation<IPaymentLine | IPaymentQueueLine>,
    productsMap: Map<string, IPOSListing>,
  ) => {
    if (!this.parkedSaleModalQueue) return

    for (const paymentLine of this.parkedSaleModalQueue.paymentLines) {
      const validationLine = createBarcodeValidation.addValidationLine(
        paymentLine,
        paymentLine.quantity,
      )

      this.processNonProductValidationLine(validationLine, productsMap, paymentLine.productId)
    }

    this.setState({ createBarcodeValidation })
  }

  processNonProductValidationLine = (
    validationLine: ValidationLine<IPaymentLine | IPaymentQueueLine>,
    productsMap: Map<string, IPOSListing>,
    productId: string | null,
  ) => {
    const product = productId ? productsMap.get(productId) : undefined

    if (!product || product.productType !== ProductType.PRODUCT) {
      validationLine.setIsValidationRequired(false)
      validationLine.setQuantityValidated(validationLine.maxQuantity)
      validationLine.setIsManuallyValidated(true)
    }
  }

  onSubmitValidation = async (paymentQueueStatus: PaymentQueueStatus) => {
    const { company, setParkedSaleStatus, flatPOSListing } = this.props.parkedSaleStore
    try {
      if (
        !company ||
        !this.parkedSaleModalQueue ||
        !this.state.createBarcodeValidation ||
        !this.parkedSaleModalQueue.locationId
      ) {
        return
      }
      const companyId = company.id
      const locationId = this.parkedSaleModalQueue.locationId
      const externalSource = this.parkedSaleModalQueue.externalSourceName

      externalSource === ExternalSourceName.DUTCHIE && this.dutchieProcessAction()

      if (this.state.paymentInfo) {
        // update paid order
        const updatedValidationLines = this.state.createBarcodeValidation
          .processedValidationLines as IPaymentLine[]
        await API.updatePayment(companyId, this.state.paymentInfo.id, {
          paymentLines: updatedValidationLines,
        })
        await setParkedSaleStatus(this.parkedSaleModalQueue, paymentQueueStatus)
        this.props.setValidationModalOpen(false)
      } else {
        // update unpaid order
        const newPayment = new CreatePayment(companyId, locationId)
        const updatePaymentLines = this.state.createBarcodeValidation
          .processedValidationLines as IPaymentQueueLine[]
        const newPaymentQueue: IPaymentQueueWithExternalSource = {
          ...this.parkedSaleModalQueue,
          paymentLines: updatePaymentLines,
        }
        if (flatPOSListing.length !== 0) {
          const productStore = this.props.currentCompanyStore?.productStore
          const categories = await productStore?.getCategories()
          newPayment.setCategories(categories || [])

          const discounts = await productStore?.getDiscounts({
            permissions: {
              canUseExternalLoyalty: !!this.props.currentCompanyStore?.canUseExternalLoyalty,
              canAccessLoyalty:
                !!this.props.currentCompanyStore?.company?.permissions.canAccessLoyalty,
            },
          })
          newPayment.setDiscounts(discounts || [])

          await newPayment.populateFromQueue(newPaymentQueue, flatPOSListing)

          await API.updatePaymentQueue(
            companyId,
            locationId,
            this.parkedSaleModalQueue.id,
            newPayment,
          )
          await setParkedSaleStatus(this.parkedSaleModalQueue, paymentQueueStatus)

          this.props.setValidationModalOpen(false)
        }
      }
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while updating the parked sale')
    }
  }

  validateScannedBarcode = (scannedCode: string) => {
    if (!this.state.createBarcodeValidation) {
      return
    }

    const cleanedBarcode = cleanBarcode(scannedCode)
    const convertedBarcode = convertToRegularBarcodeIfBatch(cleanedBarcode)
    const matchingListing = this.getMatchingListing(convertedBarcode)

    if (!matchingListing) {
      return this.displayErrorMessage(
        <>
          <strong>Barcode not found</strong>&nbsp;- There is no product with barcode {scannedCode}
        </>,
      )
    }

    const linesNeedingValidation = (
      this.state.createBarcodeValidation.validationLines as ValidationLine<
        IPaymentLine | IPaymentQueueLine
      >[]
    ).filter((item) => item.isValidationRequired && item.validationLine.productId)

    const matchingItemIndex = linesNeedingValidation.findIndex(
      (item) => item.validationLine.productId === matchingListing.id,
    )

    const name = productName(matchingListing)
    if (matchingItemIndex === -1) {
      return this.displayErrorMessage(
        <>
          <strong>{name}</strong>&nbsp;is not in the order.
        </>,
      )
    }

    const matchingValidationItem = linesNeedingValidation[matchingItemIndex]
    this.onScannedItemToFocus(matchingItemIndex)

    if (matchingValidationItem.areAllquantitiesValidated) {
      return this.displayErrorMessage(
        <>
          <strong style={{ display: 'contents' }}>{name} has already been validated.</strong>
          <br />
          Click [Apply] button to complete.
        </>,
      )
    }

    const currentQuantityValidated = matchingValidationItem.quantityValidated
    matchingValidationItem.setQuantityValidated(currentQuantityValidated + 1)
  }

  displayErrorMessage = (errorContent: JSX.Element) => {
    message.error({
      content: (
        <div style={{ display: 'inline-flex', maxWidth: '50vw', textAlign: 'left' }}>
          {errorContent}
        </div>
      ),
      duration: 3,
    })
  }

  getMatchingListing = (convertedBarcode: string): IPOSListing | undefined => {
    const { parkedSaleStore } = this.props
    const { flatPOSListing } = parkedSaleStore

    const matchingListing = flatPOSListing.find((listing) => {
      const listingBarcode = listing.barcode || ''
      const doubleZeroRemovedBarcode =
        listingBarcode.substring(0, 2) === '00'
          ? listingBarcode.substring(2, listingBarcode.length)
          : listingBarcode
      const doubledZeroRemovedConvertedBarcode =
        convertedBarcode.substring(0, 2) === '00'
          ? convertedBarcode.substring(2, convertedBarcode.length)
          : convertedBarcode
      return (
        doubleZeroRemovedBarcode.toLowerCase() === doubledZeroRemovedConvertedBarcode.toLowerCase()
      )
    })

    return matchingListing
  }

  // Show message for the specific external integrator
  dutchieProcessAction = () => {
    message.info({
      content: 'Dutchie will send SMS to a customer in 1 minute.',
      duration: 3,
    })
  }

  // Call only the product is exist
  onScannedItemToFocus = (index: number) => {
    const { refValidationContainer } = this.state
    if (!refValidationContainer || !refValidationContainer.current) return

    const focusDiv: Partial<HTMLDivElement> = refValidationContainer.current.children[index]

    if (focusDiv.offsetTop) {
      refValidationContainer.current.scrollTop = focusDiv.offsetTop
    } else {
      refValidationContainer.current.scrollTop = 0
    }
  }

  render() {
    const { isValidationModalOpen, setValidationModalOpen } = this.props
    const { areAllLinesValidated } = this.state.createBarcodeValidation || {}

    return (
      <>
        <Modal
          title={'Start scanning'}
          visible={isValidationModalOpen}
          onCancel={() => {
            setValidationModalOpen(false)
          }}
          maskClosable={false}
          footer={
            <>
              <Button
                onClick={() => {
                  setValidationModalOpen(false)
                }}
              >
                Cancel
              </Button>
              <Button
                type='primary'
                disabled={!areAllLinesValidated}
                onClick={() => {
                  if (this.parkedSaleModalQueue) {
                    const paymentQueueValue =
                      this.parkedSaleModalQueue.orderType === PaymentQueueOrderType.PICKUP
                        ? PaymentQueueStatus.READY_FOR_PICKUP
                        : PaymentQueueStatus.OUT_FOR_DELIVERY
                    this.onSubmitValidation(paymentQueueValue)
                  }
                }}
              >
                Apply
              </Button>
            </>
          }
        >
          {this.state.createBarcodeValidation && (
            <CreateValidationLines<IPaymentLine | IPaymentQueueLine>
              createBarcodeValidation={this.state.createBarcodeValidation}
              paymentInfo={this.state.paymentInfo}
              refValidationContainer={this.state.refValidationContainer}
            />
          )}

          {this.state.createBarcodeValidation &&
            getTotalProductsToValidate(this.state.createBarcodeValidation) > 0 && (
              <div style={styles.extraActionContainer}>
                {`or `}
                <Button
                  disabled={areAllLinesValidated}
                  type='link'
                  style={{ padding: 0 }}
                  onClick={() => {
                    if (window.confirm(`Are you sure?\nNot all products have been validated.`)) {
                      if (this.parkedSaleModalQueue) {
                        const paymentQueueValue =
                          this.parkedSaleModalQueue.orderType === PaymentQueueOrderType.PICKUP
                            ? PaymentQueueStatus.READY_FOR_PICKUP
                            : PaymentQueueStatus.OUT_FOR_DELIVERY
                        this.onSubmitValidation(paymentQueueValue)
                      }
                    }
                  }}
                >
                  add anyway
                </Button>
              </div>
            )}
        </Modal>
        <BarcodeListener onBarcodeEnter={this.validateScannedBarcode} />
      </>
    )
  }
}

const styles: { [key: string]: CSSProperties } = {
  extraActionContainer: {
    color: 'var(--secondary)',
    fontSize: 12,
  },
}
